/* NH Lipstick & Blush — product + collection page behaviour (draft theme).
   Progressive enhancement only: every control works as a link/form without JS. */
(() => {
  if (window.__nhpInit) return;
  window.__nhpInit = true;
  const doc = document;
  const root = doc.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const store = {
    get(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  };

  /* ---------- Sticky offset: sit below a sticky/fixed site header ---------- */
  const setTop = () => {
    let top = 0;
    const header = doc.querySelector('.site-header, #SiteHeader, [data-section-type="header"]');
    const wrap = header && (header.closest('.header-sticky-wrapper, .shopify-section-group-header-group, .site-header__element') || header);
    if (wrap) {
      const pos = getComputedStyle(wrap).position;
      if (pos === 'sticky' || pos === 'fixed') {
        const r = wrap.getBoundingClientRect();
        if (r.bottom > 0 && r.top <= 1) top = Math.round(r.bottom);
      }
    }
    root.style.setProperty('--nhp-top', `${top}px`);
  };

  /* ---------- Live region ---------- */
  let live;
  const announce = (msg) => {
    if (!live) {
      live = doc.createElement('p');
      live.className = 'nhp-sr';
      live.setAttribute('aria-live', 'polite');
      doc.body.appendChild(live);
    }
    live.textContent = '';
    requestAnimationFrame(() => { live.textContent = msg; });
  };

  /* ---------- AJAX add to bag (cards, pairs, sticky bar) ---------- */
  doc.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-nhp-atc]')) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    if (button?.getAttribute('aria-busy') === 'true') return;
    button?.setAttribute('aria-busy', 'true');
    const url = window.theme?.routes?.cartAdd || '/cart/add.js';
    try {
      const response = await fetch(url, {
        method: 'POST',
        body: new FormData(form),
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        announce(data.description || data.message || 'Sorry, that could not be added.');
        return;
      }
      announce('Added to your bag');
      button?.classList.add('is-added');
      setTimeout(() => button?.classList.remove('is-added'), 1800);
      form.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data, addToCartBtn: button }, bubbles: true }));
      doc.dispatchEvent(new CustomEvent('cart:build'));
      if (window.theme?.settings?.cartType === 'page') window.location.href = window.theme.routes.cartPage || '/cart';
    } catch (error) {
      form.submit();
    } finally {
      button?.removeAttribute('aria-busy');
    }
  });

  /* ---------- Collection page ---------- */
  const initPlp = () => {
    const plp = doc.querySelector('[data-nhp-plp]');
    if (!plp) return;

    // Sort: submit on change
    const sort = plp.querySelector('[data-nhp-sort]');
    sort?.querySelector('select')?.addEventListener('change', () => sort.submit());

    // Filter drawer
    const drawer = plp.querySelector('[data-nhp-drawer]');
    const opener = plp.querySelector('[data-nhp-filter-open]');
    if (drawer && opener && typeof drawer.showModal === 'function') {
      opener.addEventListener('click', () => {
        drawer.showModal();
        root.classList.add('nhp-lock');
        drawer.querySelector('summary, input, button')?.focus();
      });
      drawer.addEventListener('close', () => { root.classList.remove('nhp-lock'); opener.focus(); });
      drawer.querySelector('[data-nhp-filter-close]')?.addEventListener('click', () => drawer.close());
      drawer.addEventListener('click', (e) => { if (e.target === drawer) drawer.close(); });
    } else if (opener && drawer) {
      opener.addEventListener('click', () => drawer.setAttribute('open', ''));
      drawer.querySelector('[data-nhp-filter-close]')?.addEventListener('click', () => drawer.removeAttribute('open'));
    }

    // Long option lists: first 8 + "Show all", with a search box over 12
    for (const set of plp.querySelectorAll('[data-nhp-fopts]')) {
      const rows = [...set.querySelectorAll('.nhp-check')];
      const more = set.querySelector('[data-nhp-fmore]');
      const search = set.querySelector('[data-nhp-fsearch]');
      const limit = 8;
      let expanded = false;
      const render = () => {
        const q = (search?.value || '').trim().toLowerCase();
        let shown = 0;
        for (const r of rows) {
          const match = !q || r.dataset.label.includes(q);
          const keep = match && (q || expanded || shown < limit || r.querySelector('input').checked);
          r.hidden = !keep;
          if (keep) shown += 1;
        }
        if (more) more.hidden = Boolean(q) || rows.length <= limit;
      };
      more?.addEventListener('click', () => {
        expanded = !expanded;
        more.setAttribute('aria-expanded', String(expanded));
        more.textContent = expanded ? 'Show fewer' : more.dataset.label;
        render();
      });
      if (more) more.dataset.label = more.textContent.trim();
      search?.addEventListener('input', render);
      search?.addEventListener('keydown', (e) => { if (e.key === 'Enter') e.preventDefault(); });
      render();
    }

    // Live result count on the apply button
    const applyBtn = drawer?.querySelector('[data-nhp-apply]');
    const dForm = drawer?.querySelector('form');
    let countTimer = 0;
    let countCtrl = null;
    const params = () => {
      const fd = new FormData(dForm);
      const sp = new URLSearchParams();
      for (const [k, v] of fd) { if (v !== '' && !k.startsWith('nhp-')) sp.append(k, v); }
      return sp;
    };
    dForm?.addEventListener('change', (e) => {
      if (e.target.matches('[data-nhp-fsearch]')) return;
      clearTimeout(countTimer);
      countTimer = setTimeout(async () => {
        countCtrl?.abort();
        countCtrl = new AbortController();
        const sp = params();
        sp.set('section_id', plp.dataset.sectionId);
        try {
          const res = await fetch(`${dForm.getAttribute('action')}?${sp}`, { signal: countCtrl.signal });
          const html = new DOMParser().parseFromString(await res.text(), 'text/html');
          const n = parseInt(html.querySelector('.nhp-toolbar__count')?.textContent || '', 10);
          if (!Number.isNaN(n) && applyBtn) applyBtn.textContent = n === 0 ? 'No matches, adjust filters' : `Show ${n} result${n === 1 ? '' : 's'}`;
        } catch (err) { /* keep label */ }
      }, 250);
    });

    // Keep filter URLs clean: drop empty price inputs before submitting
    drawer?.querySelector('form')?.addEventListener('submit', (e) => {
      for (const input of e.target.querySelectorAll('input[type="number"]')) {
        if (input.value === '') input.disabled = true;
      }
      for (const input of e.target.querySelectorAll('[data-nhp-fsearch]')) input.disabled = true;
    });

    // Grid density (desktop only)
    const grid = plp.querySelector('[data-nhp-grid]');
    const buttons = [...plp.querySelectorAll('[data-nhp-cols]')];
    const applyCols = (n) => {
      if (!grid) return;
      grid.style.setProperty('--nhp-cols', n);
      for (const b of buttons) b.setAttribute('aria-pressed', String(b.dataset.nhpCols === String(n)));
    };
    const saved = store.get('nhp-cols');
    if (saved && ['2', '3', '4'].includes(saved)) applyCols(saved);
    for (const b of buttons) {
      b.addEventListener('click', () => { applyCols(b.dataset.nhpCols); store.set('nhp-cols', b.dataset.nhpCols); });
    }

    // Toolbar stuck state
    const bar = plp.querySelector('[data-nhp-toolbar]');
    if (bar && 'IntersectionObserver' in window) {
      const sentinel = doc.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.height = '1px';
      bar.before(sentinel);
      new IntersectionObserver(([entry]) => {
        bar.classList.toggle('is-stuck', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }).observe(sentinel);
    }

    // Load more (the numbered links stay for crawlers and no-JS)
    const more = plp.querySelector('[data-nhp-more]');
    if (more && grid) {
      more.addEventListener('click', async (event) => {
        event.preventDefault();
        if (more.getAttribute('aria-busy') === 'true') return;
        more.setAttribute('aria-busy', 'true');
        const label = more.textContent;
        more.textContent = 'Loading…';
        try {
          const res = await fetch(more.href, { headers: { Accept: 'text/html' } });
          const html = new DOMParser().parseFromString(await res.text(), 'text/html');
          const items = html.querySelectorAll('[data-nhp-grid] [data-nhp-item]');
          const first = items[0];
          for (const li of items) { li.classList.add('nhp-in'); grid.appendChild(doc.importNode(li, true)); }
          const nextMore = html.querySelector('[data-nhp-more]');
          const status = plp.querySelector('.nhp-more__status');
          const nextStatus = html.querySelector('.nhp-more__status');
          const pages = plp.querySelector('.nhp-pages');
          const nextPages = html.querySelector('.nhp-pages');
          if (pages && nextPages) pages.replaceWith(doc.importNode(nextPages, true));
          if (nextMore) {
            more.href = nextMore.href;
            more.textContent = label;
            if (status && nextStatus) status.textContent = nextStatus.textContent;
          } else {
            more.remove();
            if (status) status.textContent = `Showing all ${grid.querySelectorAll('[data-nhp-item]').length}`;
          }
          announce(`${items.length} more products loaded`);
          if (first) {
            const link = grid.querySelectorAll('[data-nhp-item]')[grid.querySelectorAll('[data-nhp-item]').length - items.length]?.querySelector('.nhp-card__link');
            link?.focus({ preventScroll: true });
          }
        } catch (e) {
          window.location.href = more.href;
        } finally {
          more.removeAttribute('aria-busy');
        }
      });
    }
  };

  /* ---------- Product page ---------- */
  const initPdp = () => {
    const pdp = doc.querySelector('[data-nhp-pdp]');
    const form = doc.querySelector('.product-single__form');
    if (!form) return;
    const mainBtn = form.querySelector('[type="submit"][name="add"], .add-to-cart');
    const atcText = mainBtn?.querySelector('[data-add-to-cart-text]');
    if (atcText && /add to cart/i.test(atcText.textContent)) {
      atcText.dataset.defaultText = 'Add to bag';
      atcText.textContent = 'Add to bag';
    }
    if (window.theme?.strings?.addToCart) window.theme.strings.addToCart = 'Add to bag';

    // Sticky add-to-bag bar (mobile)
    // PLP/PDP 2026-09-29 (taste audit T05, owner: "Add to bag must be in the first phone screen"): the bar is up
    // FROM PAGE LOAD on phones and steps down only while the in-page buy button (or, when the variant is sold out,
    // the "Notify me" box) is actually on screen above the tab bar. Sold out: the bar offers "Notify me", which
    // scrolls to that box. Replaces QA fix A (CRO-02), which showed the bar only after scrolling past the button.
    const bar = doc.querySelector('[data-nhp-sticky]');
    if (bar && mainBtn) {
      const priceSrc = doc.querySelector('.product-single__meta [data-product-price]');
      const priceOut = bar.querySelector('[data-nhp-sticky-price]');
      const addBtn = bar.querySelector('[data-nhp-sticky-add]');
      const addLabel = addBtn && addBtn.querySelector('span');
      const bisBox = () => doc.querySelector('[data-nh-bis]:not([hidden])');
      const syncPrice = () => { if (priceSrc && priceOut) priceOut.textContent = priceSrc.textContent.trim(); };
      syncPrice();
      if (priceSrc) new MutationObserver(syncPrice).observe(priceSrc, { childList: true, characterData: true, subtree: true });
      const syncState = () => {
        if (!addBtn) return;
        const soldOut = mainBtn.disabled;
        const canNotify = soldOut && !!bisBox();
        addBtn.disabled = soldOut && !canNotify;
        addBtn.classList.toggle('is-notify', canNotify);
        if (addLabel) addLabel.textContent = canNotify ? 'Notify me' : (soldOut ? 'Sold out' : 'Add to bag');
        update();
      };
      const phone = window.matchMedia('(max-width: 989px)');
      const dockBand = () => {
        const dock = doc.getElementById('NhDock');
        if (!dock || dock.hidden) return 0;
        const r = dock.getBoundingClientRect();
        if (!r.height || getComputedStyle(dock).visibility === 'hidden') return 0;
        return Math.max(0, Math.round(window.innerHeight - r.top));
      };
      // Lead 2026-09-29: never two Add to bag buttons at once. The bottom stack (blush band + buy bar + tab bar) starts at the
      // band's top edge; the buy bar steps down as soon as ANY part of the in-page button is above that line.
      const band = doc.querySelector('[data-nhp-band]');
      const setBh = () => { if (bar.offsetHeight) root.style.setProperty('--nhp-bh', `${bar.offsetHeight}px`); };
      setBh();
      const stackTop = () => {
        const b = band && band.getBoundingClientRect();
        if (b && b.height) return b.top + 8; // below the 8px fade, where the solid blush starts
        return window.innerHeight - dockBand() - bar.getBoundingClientRect().height - 8;
      };
      const target = () => (mainBtn.disabled ? (bisBox() || mainBtn) : mainBtn);
      const targetInView = () => {
        const t = target();
        const r = t.getBoundingClientRect();
        if (!r.height) return false;
        // Hysteresis: the tab bar is 8px taller while the buy bar is down, so the line moves; a 16px margin when the
        // bar is down stops it flickering in that band.
        const line = stackTop() + (bar.classList.contains('is-on') ? 0 : 16);
        // Live polish 2026-09-30 (owner: Add to bag on the first phone screen): the in-page button counts as "in view"
        // only when its whole height clears the bottom stack (or it is leaving at the top). A button peeking out from
        // under the tab bar no longer holds the bar down. The sold-out "Notify me" box keeps the any-part rule.
        if (t !== mainBtn) return r.bottom > 0 && r.top < line;
        return r.bottom > 0 && r.bottom <= line;
      };
      let io = null;
      let ioMargin = -1;
      const watchIo = () => {
        if (!('IntersectionObserver' in window)) return;
        const m = Math.max(0, Math.round(window.innerHeight - stackTop()));
        if (io && Math.abs(m - ioMargin) < 4) return;
        ioMargin = m;
        io?.disconnect();
        io = new IntersectionObserver(() => update(), { rootMargin: `0px 0px -${m}px 0px`, threshold: [0, 1] });
        io.observe(target());
      };
      let nearPal = false;
      let nearFoot = false;
      let raf = 0;
      // Final fix 2026-09-29 (R3D-01): the bar and its band never sit over an open drawer or dialog (cart drawer,
      // menu drawer, filters, search, modals). The tab bar's own overlay test sets html.nhk-away; the rest are the
      // theme's own open-state hooks.
      const OVERLAY = '#CartDrawer.drawer--is-open, .drawer.drawer--is-open, #NhDrawer.is-open, dialog[open], .modal.modal--is-active, predictive-search.nhps-open';
      const overlayOpen = () => root.classList.contains('js-drawer-open') || root.classList.contains('nhd-lock') || root.classList.contains('nhfd-lock')
        || !!doc.querySelector(OVERLAY);
      const priceEl = doc.querySelector('.product-single__meta .product-block--price') || priceSrc;
      // Live polish 2026-09-30 (CRO3-05): the bar carries the price, so when the raised bar would slice the in-page
      // price line (part above, part under it) that line fades out instead of showing a cut-off half; it fades back
      // once it clears the bar. The test uses the bar's live top edge (its fade band included), not an estimate.
      const shadePrice = () => {
        if (!priceEl) return;
        let under = false;
        if (bar.classList.contains('is-on')) {
          const r = priceEl.getBoundingClientRect();
          const b = band && band.getBoundingClientRect();
          const top = b && b.height ? b.top : bar.getBoundingClientRect().top;
          under = !!r.height && r.bottom > top && r.top < window.innerHeight;
        }
        if (priceEl.classList.contains('nhp-price-under') !== under) priceEl.classList.toggle('nhp-price-under', under);
      };
      function update() {
        const soldOut = mainBtn.disabled;
        const usable = !soldOut || addBtn?.classList.contains('is-notify');
        const show = phone.matches && usable && !nearPal && !nearFoot && !overlayOpen() && !targetInView();
        if (bar.classList.contains('is-on') !== show) {
          bar.classList.toggle('is-on', show);
          bar.setAttribute('aria-hidden', String(!show));
          bar.inert = !show;
          root.classList.toggle('nhp-sticky-on', show);
          setTimeout(queue, 420);
        }
        shadePrice();
        if (!root.classList.contains('nhp-atc-ready')) root.classList.add('nhp-atc-ready'); // no write when set (root class observer)
      }
      const queue = () => { if (raf) return; raf = requestAnimationFrame(() => { raf = 0; update(); }); };
      syncState();
      new MutationObserver(syncState).observe(mainBtn, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true });
      window.addEventListener('scroll', queue, { passive: true });
      // the price line moves while fonts and the gallery settle: re-judge then, not only on scroll
      window.addEventListener('load', queue, { once: true });
      doc.fonts?.ready?.then(queue);
      const metaBox = priceEl && priceEl.closest('.product-single__meta');
      if (metaBox && window.ResizeObserver) new ResizeObserver(queue).observe(metaBox);
      new MutationObserver(queue).observe(root, { attributes: true, attributeFilter: ['class'] });
      const cd = doc.getElementById('CartDrawer');
      if (cd) new MutationObserver(queue).observe(cd, { attributes: true, attributeFilter: ['class', 'open', 'aria-hidden'] });
      window.addEventListener('resize', () => { setBh(); watchIo(); queue(); }, { passive: true });
      watchIo();
      phone.addEventListener?.('change', queue);
      doc.addEventListener('variant:change', () => setTimeout(() => { syncState(); ioMargin = -1; watchIo(); }, 0));
      if ('IntersectionObserver' in window) {
        const palBox = doc.querySelector('[data-nhp-pal]');
        if (palBox) new IntersectionObserver(([e]) => { nearPal = e.isIntersecting && palBox.classList.contains('is-open'); update(); }, { rootMargin: '0px 0px -10% 0px', threshold: [0, .25, .5] }).observe(palBox);
        const foot = doc.querySelector('footer, .site-footer, .nhf');
        if (foot) new IntersectionObserver(([e]) => { nearFoot = e.isIntersecting; update(); }).observe(foot);
      }
      addBtn?.addEventListener('click', () => {
        if (!mainBtn.disabled) { mainBtn.click(); return; }
        const box = bisBox();
        if (!box) return;
        box.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        const input = box.querySelector('input[type="email"]');
        if (input) setTimeout(() => input.focus({ preventScroll: true }), 350);
      });
      // Fix batch 2026-09-29 (FE-F08): the bar's thumbnail follows the chosen variant's photo.
      const stickyImg = bar.querySelector('img.nhp-sticky__img');
      doc.addEventListener('variant:change', (e) => {
        const src = e.detail?.variant?.featured_image?.src;
        if (!stickyImg || !src) return;
        const url = new URL(src, location.href);
        url.searchParams.set('width', '120');
        stickyImg.removeAttribute('srcset');
        stickyImg.removeAttribute('sizes');
        stickyImg.src = url.href;
      });
    }

    // Fix batch 2026-09-29 (FE-F02): the Afterpay app block renders once from the first variant's price
    // and never hears about variant changes, so "4 x $14.50" stayed on a $77.99 variant. The placement is
    // a custom element that re-renders when its data-amount attribute changes.
    doc.addEventListener('variant:change', (e) => {
      const cents = e.detail?.variant?.price;
      if (typeof cents !== 'number') return;
      const amount = (cents / 100).toFixed(2);
      for (const el of doc.querySelectorAll('.product-single__meta square-placement, .product-single__meta afterpay-placement, .product-section square-placement, .product-section afterpay-placement')) {
        if (el.getAttribute('data-amount') !== amount) el.setAttribute('data-amount', amount);
      }
    });

    // Trust ticker: static when it fits; pause on touch/hover/focus and off-screen
    const trust = doc.querySelector('[data-nhp-trust]');
    if (trust) {
      const list = trust.querySelector('.nhp-trust__list');
      const fit = () => { trust.classList.toggle('is-static', list.scrollWidth <= trust.clientWidth + 1); };
      fit();
      window.addEventListener('resize', fit, { passive: true });
      const pause = (v) => trust.classList.toggle('is-paused', v);
      trust.addEventListener('pointerdown', () => pause(true));
      trust.addEventListener('pointerup', () => setTimeout(() => pause(false), 1500));
      trust.addEventListener('pointercancel', () => pause(false));
      if ('IntersectionObserver' in window) new IntersectionObserver(([e]) => pause(!e.isIntersecting)).observe(trust);
    }

    // Ask Nova (Dori Product Pal) moved to assets/nh-pdp-nova.js (rendered with the card in
    // snippets/nh-pdp-buybox, part 'pal'); reviews/Loox loading moved to assets/nh-pdp-reviews.js
    // (sections/nh-pdp-reviews). 2026-09-28 speed pass.

    if (!pdp) return;
    const handle = pdp.dataset.handle;

    // Recommendations (Shopify's recommendations endpoint + section rendering)
    const recs = doc.querySelector('[data-nhp-recs]');
    if (recs && recs.dataset.url) {
      const load = async () => {
        try {
          let fresh = null;
          try {
            const res = await fetch(recs.dataset.url);
            const html = new DOMParser().parseFromString(await res.text(), 'text/html');
            fresh = html.querySelector('[data-nhp-recs-list]');
          } catch (e) { /* fall back to the category picks below */ }
          const slot = recs.querySelector('[data-nhp-recs-slot]');
          // Fix batch C (CRO-25): keep only related picks from this product's own category
          // collection (data-cat), topped up from that collection (<template>), so a clitoral
          // toy never shows a dildo or a stroker. Without a category, Shopify's order is kept.
          const lim = Number(recs.dataset.limit) || 4;
          const cat = recs.dataset.cat;
          const tpl = recs.querySelector('template[data-nhp-recs-fallback]');
          let picks = fresh ? [...fresh.children] : [];
          if (cat) {
            const seen = new Set();
            const same = picks.filter((li) => (li.dataset.cols || '').includes(`|${cat}|`) && !seen.has(li.dataset.pid) && seen.add(li.dataset.pid));
            for (const li of tpl ? [...tpl.content.children] : []) {
              if (same.length >= lim) break;
              if (!seen.has(li.dataset.pid)) { seen.add(li.dataset.pid); same.push(li); }
            }
            if (same.length >= 2) picks = same;
          }
          picks = picks.slice(0, lim);
          if (picks.length) {
            if (!fresh) { fresh = doc.createElement('ul'); fresh.className = 'nhp-recs__list nhp-grid-list'; fresh.setAttribute('role', 'list'); fresh.setAttribute('data-nhp-recs-list', ''); }
            fresh.replaceChildren(...picks.map((li) => { li.hidden = false; return li; }));
          }
          if (fresh && fresh.children.length && slot) {
            const show = () => { slot.replaceWith(doc.importNode(fresh, true)); recs.hidden = false; };
            // If the answer arrives while the (still empty) slot is on screen, e.g. a quick flick to the
            // footer on a slow connection, wait until it is clear of the viewport so the footer never jumps.
            const clear = () => { const r = (recs.parentElement || recs).getBoundingClientRect(); return r.top > window.innerHeight + 200 || r.bottom < -200; };
            if (clear()) show();
            else {
              let raf = 0;
              const onScroll = () => {
                cancelAnimationFrame(raf);
                raf = requestAnimationFrame(() => { if (clear()) { window.removeEventListener('scroll', onScroll); show(); } });
              };
              window.addEventListener('scroll', onScroll, { passive: true });
            }
          }
        } catch (e) { /* leave hidden */ }
      };
      // Fetch early (idle after first paint, or ~1,500px before the section) so the cards are in
      // place long before anyone scrolls down to them: no late insert above the footer (QA r2 N4).
      let started = false;
      let io = null;
      const go = () => { if (started) return; started = true; io?.disconnect(); load(); };
      if ('IntersectionObserver' in window) {
        io = new IntersectionObserver(([e]) => { if (e.isIntersecting) go(); }, { rootMargin: '1500px 0px' });
        io.observe(recs.parentElement || recs);
      }
      if ('requestIdleCallback' in window) window.requestIdleCallback(go, { timeout: 800 });
      else setTimeout(go, 600);
    }

    // Recently viewed (stored locally, rendered from what we saved — no extra requests)
    const key = 'nhp-rv';
    let list = [];
    try { list = JSON.parse(store.get(key) || '[]'); } catch (e) { list = []; }
    let me = null;
    try { me = pdp.dataset.rv ? JSON.parse(pdp.dataset.rv) : null; } catch (e) { me = null; } // fix E: never let a bad payload stop the page script
    const rv = doc.querySelector('[data-nhp-rv]');
    if (rv) {
      const others = list.filter((p) => p && p.h !== handle).slice(0, 8);
      const ul = rv.querySelector('ul');
      if (others.length >= 2 && ul) {
        for (const p of others) {
          const li = doc.createElement('li');
          li.className = 'nhp-rv__item';
          const a = doc.createElement('a');
          a.className = 'nhp-rv__link';
          a.href = p.u;
          let img;
          if (p.i) {
            img = doc.createElement('img');
            img.className = 'nhp-rv__img';
            img.src = p.i; img.alt = ''; img.loading = 'lazy'; img.width = 160; img.height = 160;
          } else {
            // fix E (NH-QA-001): product with no photo = the branded blush tile (.nh-pph, nh-site-fixes)
            img = doc.createElement('span');
            img.className = 'nh-pph nh-pph--sm nhp-rv__img';
            img.setAttribute('aria-hidden', 'true');
          }
          const t = doc.createElement('span');
          t.className = 'nhp-rv__title'; t.textContent = p.t;
          const pr = doc.createElement('span');
          pr.className = 'nhp-rv__price'; pr.textContent = p.p;
          a.append(img, t, pr);
          li.appendChild(a);
          ul.appendChild(li);
        }
        rv.hidden = false;
      }
    }
    if (me) {
      list = [me, ...list.filter((p) => p && p.h !== me.h)].slice(0, 12);
      store.set(key, JSON.stringify(list));
    }
  };

  /* ---------- Gentle reveal ---------- */
  const initReveal = () => {
    if (reduced || !('IntersectionObserver' in window)) return;
    const els = doc.querySelectorAll('[data-nhp-reveal]');
    if (!els.length) return;
    root.classList.add('nhp-reveal-on');
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      }
    }, { rootMargin: '0px 0px -8% 0px' });
    for (const el of els) {
      if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-in');
      else io.observe(el);
    }
  };

  /* ---------- Motion (2026-09-28): PDP "lift off the plinth" + card depth + grid reveal ----------
     CSS lives in nh-i-pdp.css / nh-i-card.css / nh-i-plp.css. Everything here only adds
     classes and CSS variables; nothing is hidden before first paint, and with reduced
     motion only fades remain. Listeners and observers run only while they are needed. */
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
  const hasIO = 'IntersectionObserver' in window;

  // White-key SVG filter: alpha = 12 * (3 - R - G - B), so only near-pure packshot white clears.
  // Lets a product cross its frame edge without its white ground. html.nhm-key gates the CSS.
  const addKey = () => {
    if (doc.getElementById('nhm-key')) return;
    const svg = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('width', '0');
    svg.setAttribute('height', '0');
    svg.style.position = 'absolute';
    svg.innerHTML = '<filter id="nhm-key" color-interpolation-filters="sRGB"><feColorMatrix type="matrix" values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 -12 -12 -12 36 0"/></filter>';
    doc.body.appendChild(svg);
    root.classList.add('nhm-key');
  };

  // Fix batch 2026-09-29 (D-22): flag model / lifestyle photos (corners not white or transparent) so the
  // packshot plinth, glow ring and multiply blend are not painted over them. Reads a 24px copy of each
  // image (same-origin /cdn/shop URL, so the canvas is not tainted); anything unreadable stays a packshot.
  const flagPhotos = (frame) => {
    if (frame.classList.contains('nhp-gallery--worn')) return;
    const test = (slide, src) => {
      const probe = new Image();
      probe.decoding = 'async';
      probe.onload = () => {
        try {
          const c = doc.createElement('canvas');
          c.width = 24; c.height = 24;
          const x = c.getContext('2d', { willReadFrequently: true });
          x.drawImage(probe, 0, 0, 24, 24);
          const d = x.getImageData(0, 0, 24, 24).data;
          let dark = 0;
          for (const [px, py] of [[1, 1], [22, 1], [1, 22], [22, 22], [12, 1], [1, 12], [22, 12]]) {
            const i = (py * 24 + px) * 4;
            if (d[i + 3] > 40 && Math.min(d[i], d[i + 1], d[i + 2]) < 225) dark++;
          }
          if (dark >= 3) slide.classList.add('nhm-photo');
        } catch (e) { /* tainted or unreadable: leave as packshot */ }
      };
      const u = new URL(src, location.href);
      if (u.origin !== location.origin) return;
      u.searchParams.set('width', '48');
      probe.src = u.href;
    };
    for (const slide of frame.querySelectorAll('.product-main-slide')) {
      const img = slide.querySelector('img');
      const src = img && (img.currentSrc || img.getAttribute('src') || img.dataset.src);
      if (src) test(slide, src);
    }
  };

  const initPdpMotion = () => {
    const frame = doc.querySelector('.template-product .product__main-photos');
    const show = frame?.querySelector('[data-product-photos]');
    if (!frame || !show) return;
    if ('requestIdleCallback' in window) window.requestIdleCallback(() => flagPhotos(frame), { timeout: 1500 });
    else setTimeout(() => flagPhotos(frame), 800);
    frame.classList.add('nhm-pdp');
    { const hero = (frame.querySelector('.product-main-slide.starting-slide') || frame.querySelector('.product-main-slide'))?.querySelector('img'); if (hero) hero.setAttribute('elementtiming', 'nhm-hero'); }
    const slides = () => frame.querySelectorAll('.product-main-slide');
    // before Flickity starts, the theme shows the chosen variant's image as .starting-slide
    const selected = () => frame.querySelector('.product-main-slide.is-selected') || frame.querySelector('.product-main-slide.starting-slide') || slides()[0];
    let current = null;
    let entered = false;
    let scrollDepth = null;

    // Slide change: the new image comes forward, the old one recedes.
    const swap = (next) => {
      const prev = current;
      current = next;
      if (!entered || !next) return;
      if (prev && prev !== next) {
        prev.classList.remove('nhm-in', 'nhm-emerge');
        prev.classList.add('nhm-out');
        setTimeout(() => prev.classList.remove('nhm-out'), 340);
      }
      next.classList.remove('nhm-out');
      next.classList.add('nhm-in');
      setTimeout(() => next.classList.remove('nhm-in'), 500);
    };
    new MutationObserver(() => {
      const s = frame.querySelector('.product-main-slide.is-selected');
      if (s && s !== current) swap(s);
    }).observe(show, { subtree: true, attributes: true, attributeFilter: ['class'] });

    // Entrance: once the selected image has decoded and the gallery is on screen.
    const enter = () => {
      if (entered) return;
      entered = true;
      current = selected();
      if (reduced || !current) return; // reduced motion: no entrance, no idle float
      const slide = current;
      slide.classList.add('nhm-emerge');
      setTimeout(() => {
        slide.classList.remove('nhm-emerge');
        frame.classList.add('nhm-float');
      }, 1000);
    };
    const ready = () => {
      const img = selected()?.querySelector('img');
      // 2026-09-28 perf: Flickity re-inserts the slide when it starts, and Chrome treats the re-inserted
      // <img> as a new Largest Contentful Paint candidate. If the entrance was already moving it, that
      // first paint was a little larger than the resting one and became a late LCP (Manta: +2.5s).
      // So the entrance waits for this image's own paint (Element Timing), capped at 1s.
      const go = () => requestAnimationFrame(() => requestAnimationFrame(enter));
      if (!img) { go(); return; }
      let done = false;
      let po = null;
      const fire = () => { if (done) return; done = true; clearTimeout(cap); if (po) po.disconnect(); go(); };
      const cap = setTimeout(fire, 1000);
      try {
        po = new PerformanceObserver((list) => { if (list.getEntries().some((e) => e.element === img)) fire(); });
        po.observe({ type: 'element', buffered: true });
      } catch (e) { /* no Element Timing: the 1s cap starts it */ }
    };
    // Wait for Flickity to pick the starting slide before the entrance, max 5s (2026-09-28 perf: a
    // 0.6s cap let the scale-up start before Flickity rebuilt the slide, so the rebuilt image painted
    // ~10% larger and became a late LCP candidate);
    // a later jump just plays the slide-change depth transition.
    const whenSlider = () => {
      if (show.classList.contains('flickity-enabled') || !slides().length) { ready(); return; }
      const mo = new MutationObserver(() => { if (show.classList.contains('flickity-enabled')) { mo.disconnect(); clearTimeout(t); ready(); } });
      mo.observe(show, { attributes: true, attributeFilter: ['class'] });
      const t = setTimeout(() => { mo.disconnect(); ready(); }, 5000);
    };
    if (hasIO) {
      const vis = new IntersectionObserver(([e]) => {
        frame.classList.toggle('nhm-paused', !e.isIntersecting);
        if (e.isIntersecting && !entered && !frame.dataset.nhmWait) { frame.dataset.nhmWait = '1'; whenSlider(); }
        if (scrollDepth) scrollDepth(e.isIntersecting);
      });
      vis.observe(frame);
    } else {
      whenSlider();
    }

    if (reduced) return;
    addKey();

    // Desktop: the product tilts toward the pointer.
    if (finePointer) {
      let rect = null;
      let raf = 0;
      let px = 0;
      let py = 0;
      frame.addEventListener('pointerenter', () => { rect = frame.getBoundingClientRect(); frame.classList.add('nhm-tilting'); });
      frame.addEventListener('pointermove', (e) => {
        px = e.clientX; py = e.clientY;
        if (raf || !rect) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          const x = Math.min(Math.max((px - rect.left) / rect.width - 0.5, -0.5), 0.5);
          const y = Math.min(Math.max((py - rect.top) / rect.height - 0.5, -0.5), 0.5);
          frame.style.setProperty('--nhm-ry', `${(x * 14).toFixed(2)}deg`);
          frame.style.setProperty('--nhm-rx', `${(-y * 12).toFixed(2)}deg`);
        });
      });
      frame.addEventListener('pointerleave', () => {
        cancelAnimationFrame(raf); raf = 0; rect = null;
        frame.classList.remove('nhm-tilting');
        frame.style.removeProperty('--nhm-rx');
        frame.style.removeProperty('--nhm-ry');
      });
    }

    // Phones: scrolling the gallery away sinks the product back into the page.
    if (coarsePointer && !finePointer) {
      let raf = 0;
      let on = false;
      const update = () => {
        raf = 0;
        const r = frame.getBoundingClientRect();
        const p = Math.min(Math.max((window.innerHeight * 0.22 - r.top) / r.height, 0), 1);
        frame.style.setProperty('--nhm-p', p.toFixed(3));
      };
      const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
      scrollDepth = (visible) => {
        if (visible === on) return;
        on = visible;
        frame.classList.toggle('nhm-scroll', visible);
        if (visible) { window.addEventListener('scroll', onScroll, { passive: true }); onScroll(); } else window.removeEventListener('scroll', onScroll);
      };
      if (!hasIO) scrollDepth(true);
    }
  };

  // Cards (collection grid, recommendations): pointer tilt + touch-and-hold peek.
  const initCardMotion = () => {
    if (reduced || !doc.querySelector('.nhp-card')) return;
    addKey();
    if (finePointer) {
      let card = null;
      let rect = null;
      let raf = 0;
      let px = 0;
      let py = 0;
      const leave = () => {
        if (!card) return;
        card.classList.remove('nhm-tilt');
        card.style.removeProperty('--nhm-rx');
        card.style.removeProperty('--nhm-ry');
        card = null; rect = null;
      };
      doc.addEventListener('pointerover', (e) => {
        if (e.pointerType !== 'mouse') return;
        const c = e.target.closest?.('.nhp-card');
        if (c === card) return;
        leave();
        if (!c) return;
        card = c;
        rect = c.querySelector('.nhp-card__stage')?.getBoundingClientRect() || c.getBoundingClientRect();
      }, { passive: true });
      doc.addEventListener('pointermove', (e) => {
        if (!card) return;
        px = e.clientX; py = e.clientY;
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          if (!card || !rect) return;
          const x = Math.min(Math.max((px - rect.left) / rect.width - 0.5, -0.5), 0.5);
          const y = Math.min(Math.max((py - rect.top) / rect.height - 0.5, -0.5), 0.5);
          card.classList.add('nhm-tilt');
          card.style.setProperty('--nhm-ry', `${(x * 12).toFixed(2)}deg`);
          card.style.setProperty('--nhm-rx', `${(-y * 12).toFixed(2)}deg`);
        });
      }, { passive: true });
      doc.addEventListener('pointerout', (e) => {
        if (card && !card.contains(e.relatedTarget)) leave();
      }, { passive: true });
      window.addEventListener('scroll', () => { if (card) rect = card.querySelector('.nhp-card__stage')?.getBoundingClientRect() || null; }, { passive: true });
    }
    // Touch: hold ~220ms to peek (image lifts, second image swaps in); a scroll or quick tap is untouched.
    let held = null;
    let timer = 0;
    let sx = 0;
    let sy = 0;
    const release = () => { clearTimeout(timer); timer = 0; held?.classList.remove('is-held'); held = null; };
    doc.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'touch') return;
      const c = e.target.closest?.('.nhp-card');
      if (!c || e.target.closest('button, form, .nhp-card__add')) return;
      release();
      sx = e.clientX; sy = e.clientY;
      timer = setTimeout(() => { held = c; c.classList.add('is-held'); }, 220);
    }, { passive: true });
    doc.addEventListener('pointermove', (e) => {
      if (e.pointerType === 'touch' && (timer || held) && Math.hypot(e.clientX - sx, e.clientY - sy) > 10) release();
    }, { passive: true });
    for (const t of ['pointerup', 'pointercancel']) doc.addEventListener(t, (e) => { if (e.pointerType === 'touch') release(); }, { passive: true });
    // A held peek is not a request for the link menu or a navigation.
    doc.addEventListener('contextmenu', (e) => { if (held || timer) e.preventDefault(); });
    doc.addEventListener('click', (e) => {
      if (!e.target.closest?.('.nhp-card.nhm-was-held')) return;
      e.preventDefault();
      e.target.closest('.nhp-card').classList.remove('nhm-was-held');
    }, true);
    doc.addEventListener('pointerup', (e) => {
      if (e.pointerType !== 'touch') return;
      const c = e.target.closest?.('.nhp-card');
      if (c && c.classList.contains('is-held')) { c.classList.add('nhm-was-held'); setTimeout(() => c.classList.remove('nhm-was-held'), 400); }
    }, { capture: true, passive: true });
  };

  // Collection grid: below-the-fold cards tip up into place in small staggered batches.
  const initGridReveal = () => {
    const grid = doc.querySelector('[data-nhp-plp] [data-nhp-grid]');
    if (!grid || !hasIO) return;
    const fold = window.innerHeight;
    const items = [...grid.querySelectorAll(':scope > .nhp-grid__item')].filter((li) => li.getBoundingClientRect().top > fold);
    if (!items.length) return;
    for (const li of items) li.classList.add('nhm-rv');
    let pending = items.length;
    const io = new IntersectionObserver((entries) => {
      const batch = entries.filter((e) => e.isIntersecting).map((e) => e.target)
        .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
      batch.forEach((li, i) => {
        io.unobserve(li);
        li.style.setProperty('--nhm-d', `${Math.min(i, 5) * 70}ms`);
        li.classList.add('nhm-rv-in');
        pending -= 1;
        // hand the card back to its normal hover transitions once it has landed
        setTimeout(() => { li.classList.remove('nhm-rv', 'nhm-rv-in'); li.style.removeProperty('--nhm-d'); }, 1200);
      });
      if (pending <= 0) io.disconnect();
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.01 });
    for (const li of items) io.observe(li);
    // Never leave anything hidden for print or if the observer is starved.
    window.addEventListener('beforeprint', () => { for (const li of items) li.classList.remove('nhm-rv'); io.disconnect(); });
  };

  // Fix batch 2026-09-29 (D-19): galleries with more than 6 photos show a "3 / 12" counter instead of a row of
  // tiny dots (phones; desktop uses the thumbnail strip).
  const initGalleryCount = () => {
    const frame = doc.querySelector('.template-product .product__main-photos');
    if (!frame) return;
    const setup = () => {
      const list = frame.querySelector('.flickity-page-dots');
      if (!list || list.children.length <= 6) return !!list;
      if (frame.querySelector('.nhp-gcount')) return true;
      list.classList.add('nhp-dots--count');
      const out = doc.createElement('span');
      out.className = 'nhp-gcount';
      out.setAttribute('aria-hidden', 'true');
      frame.appendChild(out);
      const upd = () => {
        const i = [...list.children].findIndex((d) => d.classList.contains('is-selected'));
        out.textContent = `${Math.max(1, i + 1)} / ${list.children.length}`;
      };
      upd();
      new MutationObserver(upd).observe(list, { subtree: true, attributes: true, attributeFilter: ['class'] });
      return true;
    };
    if (setup()) return;
    const mo = new MutationObserver(() => { if (setup()) mo.disconnect(); });
    mo.observe(frame, { childList: true, subtree: true });
    setTimeout(() => mo.disconnect(), 10000);
  };

  // Fix batch 2026-09-29 (FE-F12): closing the zoom view dropped focus to <body>; it returns to the zoom button.
  const initZoomFocus = () => {
    const pswp = doc.querySelector('.pswp');
    if (!pswp) return;
    let opener = null;
    doc.addEventListener('click', (e) => {
      const z = e.target instanceof Element ? e.target.closest('.product__photo-zoom, .js-photoswipe__zoom, [data-photoswipe]') : null;
      if (z) opener = z;
    }, true);
    let open = pswp.classList.contains('pswp--open');
    new MutationObserver(() => {
      const now = pswp.classList.contains('pswp--open');
      if (open && !now && opener && opener.isConnected) {
        const back = opener;
        setTimeout(() => { if (!pswp.classList.contains('pswp--open')) back.focus({ preventScroll: true }); }, 60);
      }
      open = now;
    }).observe(pswp, { attributes: true, attributeFilter: ['class'] });
  };

  const start = () => {
    setTop();
    initGalleryCount();
    initZoomFocus();
    initPlp();
    initPdp();
    initReveal();
    initPdpMotion();
    initCardMotion();
    initGridReveal();
    let raf = 0;
    window.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(setTop); }, { passive: true });
    window.addEventListener('resize', setTop, { passive: true });
  };
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start);
  else start();
})();
